import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { v4 as uuidv4 } from "uuid";
import { useAuth } from "../App";
import { api } from "../services/apiClient";
import { getAssignedPlan } from "../constants/plans";

export const usePropertyForm = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const draftStorageKey = `draft:add-property:${user?.id || user?.email || "anon"}`;

  const [loading, setLoading] = useState(false);
  const [generatingAI, setGeneratingAI] = useState(false);
  const [developerProfile, setDeveloperProfile] = useState(null);
  const [checkingLimit, setCheckingLimit] = useState(true);
  const [myDevelopments, setMyDevelopments] = useState([]);
  const [showPropertyLimitModal, setShowPropertyLimitModal] = useState(false);
  const [limitModalConfig, setLimitModalConfig] = useState(null);
  const [formData, setFormData] = useState({
    id: uuidv4(),
    title: "",
    description: "",
    price: "",
    currency: "MXN",
    price_on_request: false,
    category: "Residencial",
    property_type: "",
    estado: "",
    operation_types: ["venta"],
    address: "",
    location_type: "",
    geometry: {
      type: "Point",
      coordinates: [-99.1332, 19.4326],
    },
    land_size_m2: "",
    construction_size_m2: "",
    bedrooms: "",
    bathrooms: "",
    half_bathrooms: "",
    parking: "",
    features: "",
    amenities: "",
    image_urls: "",
    cover_image_url: "",
    developer_id: "",
    development_id: "",
    allow_broker_representation: false,
    commission_percentage: 3.0,
    max_brokers: 3,
  });

  const checkPropertyLimit = useCallback(async () => {
    if (
      ["owner", "broker", "developer"].includes(user?.role) &&
      user?.entitlements &&
      !user.entitlements.can_publish_properties
    ) {
      toast.error(
        "Tu suscripción no está activa. Activa tu suscripción para publicar propiedades.",
      );
      navigate("/account?tab=subscription");
      return;
    }

    // Basic: 1 property limit
    if (user?.role === "basic") {
      try {
        const response = await api.get("/properties/my/all");
        if (response.ok) {
          const properties = await response.json();
          if (properties.length >= 1) {
            setShowPropertyLimitModal(true);
            setLimitModalConfig({
              title: "Límite de propiedades alcanzado",
              current: properties.length,
              limit: 1,
              plan: "Básico",
              upgradePlan: "Pro",
            });
            setCheckingLimit(false);
            return;
          }
        }
      } catch (error) {
        console.error("Error checking limit:", error);
      }
    }

    // Pro (owner): 10 property limit
    if (user?.role === "owner" && getAssignedPlan(user) === "pro") {
      try {
        const response = await api.get("/properties/my/all");
        if (response.ok) {
          const properties = await response.json();
          const activeCount = properties.filter((p) => p.is_active !== false).length;
          if (activeCount >= 10) {
            setShowPropertyLimitModal(true);
            setLimitModalConfig({
              title: "Límite de propiedades alcanzado",
              current: activeCount,
              limit: 10,
              plan: "Pro",
              upgradePlan: "Asesor Inmobiliario o Desarrolladora",
            });
            setCheckingLimit(false);
            return;
          }
        }
      } catch (error) {
        console.error("Error checking limit:", error);
      }
    }
    setCheckingLimit(false);
  }, [user, navigate]);

  const fetchDeveloperProfile = useCallback(async () => {
    try {
      const response = await api.get("/developers/me/profile");
      if (response.ok) {
        const data = await response.json();
        setDeveloperProfile(data);
        setFormData((prev) => ({ ...prev, developer_id: data.id }));
      }
    } catch (error) {
      console.error("Error fetching developer profile:", error);
    }
  }, []);

  const fetchMyDevelopments = useCallback(async () => {
    try {
      const response = await api.get("/developments/my/all");
      if (response.ok) {
        const data = await response.json();
        setMyDevelopments(Array.isArray(data) ? data : []);
      }
    } catch (error) {
      console.error("Error fetching developments:", error);
    }
  }, []);

  useEffect(() => {
    fetchDeveloperProfile();
    checkPropertyLimit();
    fetchMyDevelopments();
  }, [fetchDeveloperProfile, checkPropertyLimit, fetchMyDevelopments]);

  const generateWithAI = useCallback(async () => {
    if (!formData.title || !formData.category) {
      toast.error("Completa al menos título y categoría");
      return;
    }

    setGeneratingAI(true);
    try {
      const response = await api.post("/properties/generate-description", {
        title: formData.title,
        category: formData.category,
        property_type: formData.property_type,
        address: formData.address,
        price: formData.price,
        currency: formData.currency,
        price_on_request: formData.price_on_request,
        land_size_m2: formData.land_size_m2,
        construction_size_m2: formData.construction_size_m2,
        bedrooms: formData.bedrooms,
        bathrooms: formData.bathrooms,
        parking: formData.parking,
        features: formData.features,
      });

      if (!response.ok) {
        throw new Error("Error al generar");
      }

      const data = await response.json();
      if (data.description) {
        setFormData((prev) => ({ ...prev, description: data.description }));
        toast.success("¡Descripción generada con IA!");
      }
    } catch (error) {
      console.error("IA Error:", error);
      toast.error("Error al generar descripción. Verifica tu plan.");
    } finally {
      setGeneratingAI(false);
    }
  }, [formData]);

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      setLoading(true);

      try {
        const propertyData = {
          ...formData,
          operation_types:
            formData.operation_types.length > 0
              ? formData.operation_types
              : ["venta"],
          status: formData.operation_types[0] || "venta",
          price:
            formData.price_on_request || !formData.price
              ? null
              : parseFloat(formData.price),
          currency: formData.currency || "MXN",
          price_on_request: !!formData.price_on_request,
          price_mxn:
            !formData.price_on_request &&
            formData.currency === "MXN" &&
            formData.price
              ? parseFloat(formData.price)
              : null,
          land_size_m2: formData.land_size_m2
            ? parseFloat(formData.land_size_m2)
            : null,
          construction_size_m2: formData.construction_size_m2
            ? parseFloat(formData.construction_size_m2)
            : null,
          bedrooms: formData.bedrooms ? parseInt(formData.bedrooms) : null,
          bathrooms: formData.bathrooms ? parseInt(formData.bathrooms) : null,
          half_bathrooms: formData.half_bathrooms
            ? parseInt(formData.half_bathrooms)
            : null,
          parking: formData.parking ? parseInt(formData.parking) : null,
          features: formData.features
            ? formData.features.split(",").map((f) => f.trim())
            : [],
          amenities: formData.amenities
            ? formData.amenities.split(",").map((a) => a.trim())
            : [],
          image_urls: (() => {
            const urls = formData.image_urls
              ? formData.image_urls
                  .split(",")
                  .map((u) => u.trim())
                  .filter((u) => u)
              : [];
            if (formData.cover_image_url && formData.cover_image_url.trim()) {
              const cover = formData.cover_image_url.trim();
              const filtered = urls.filter((u) => u !== cover);
              return [cover, ...filtered];
            }
            return urls;
          })(),
          commission_percentage: formData.allow_broker_representation
            ? parseFloat(formData.commission_percentage) || 3.0
            : null,
          max_brokers: formData.allow_broker_representation
            ? parseInt(formData.max_brokers) || 3
            : 3,
          development_id: formData.development_id || null,
        };

        const response = await api.post("/properties", propertyData);

        if (!response.ok) {
          let errorMessage = "Error al crear propiedad";
          try {
            const errorData = await response.json();
            errorMessage = errorData.detail || errorMessage;
          } catch {
            errorMessage = `Error ${response.status}: ${response.statusText}`;
          }
          throw new Error(errorMessage);
        }

        await response.json();
        toast.success("¡Propiedad creada exitosamente!");
        localStorage.removeItem(draftStorageKey);
        navigate("/dashboard");
      } catch (error) {
        toast.error(error.message);
      } finally {
        setLoading(false);
      }
    },
    [draftStorageKey, formData, navigate],
  );

  return {
    user,
    loading,
    generatingAI,
    developerProfile,
    checkingLimit,
    myDevelopments,
    formData,
    setFormData,
    generateWithAI,
    handleSubmit,
    showPropertyLimitModal,
    setShowPropertyLimitModal,
    limitModalConfig,
    draftStorageKey,
  };
};
